import { Divider } from "@mui/material";
import React from "react";
import Article from "../../components/Article";
import TextLink from "../../components/TextLink";
import lambdaSqsImage from "../../assets/images/aws-sqs-lambda.png";

export const LambdaSqsEventProcessingDetails = {
  articleTitle: "Lambda and SQS for Event Processing",
  articleDate: "2026-02-12",
  articleAuthor: "Harrison Cannon",
  articleImage: lambdaSqsImage,
  articleBody: "Build reliable serverless background processing with AWS Lambda and Amazon SQS, including retries, DLQs, and infrastructure as code.",
  articleSlug: "lambda-sqs-event-processing"
};

function LambdaSqsEventProcessing() {
  function ArticleContent() {
    return (
      <div>
        <h2>Background</h2>

        <p>
          <TextLink url="https://aws.amazon.com/lambda/" text="AWS Lambda" /> and <TextLink url="https://aws.amazon.com/sqs/" text="Amazon SQS" /> are a proven serverless pattern for background workloads. Instead of processing everything inline with a user request, you place work onto a queue and let Lambda scale workers to process messages asynchronously.
        </p>

        <p>
          This reduces coupling between services, improves resilience during traffic spikes, and gives you retry behavior out of the box.
        </p>

        <Divider className="divider" />

        <h2>Architecture overview</h2>

        <ol>
          <li>An API or upstream service sends a message to an SQS queue.</li>
          <li>Lambda polls the queue and processes messages in batches.</li>
          <li>If processing fails, Lambda retries based on queue visibility timeout and redrive policy.</li>
          <li>After repeated failures, messages move to a dead-letter queue (DLQ) for investigation.</li>
        </ol>

        <p>
          AWS docs worth bookmarking: <TextLink url="https://docs.aws.amazon.com/lambda/latest/dg/with-sqs.html" text="Using Lambda with SQS" />, <TextLink url="https://docs.aws.amazon.com/AWSSimpleQueueService/latest/SQSDeveloperGuide/sqs-dead-letter-queues.html" text="SQS dead-letter queues" />, and <TextLink url="https://docs.aws.amazon.com/lambda/latest/dg/services-sqs-configure.html" text="Event source mapping configuration" />.
        </p>

        <Divider className="divider" />

        <h2>Use cases and why decoupling matters</h2>

        <p>
          Decoupling means your upstream service does not need to wait for downstream processing to finish. It publishes an event to SQS and moves on, while Lambda consumers process work independently. This separation is one of the key reasons serverless architectures remain stable under variable traffic and partial failures.
        </p>

        <p>
          Common use cases include:
        </p>

        <ul>
          <li><strong>Order processing:</strong> Accept an order immediately, then process payment checks, inventory updates, and notifications asynchronously.</li>
          <li><strong>Media pipelines:</strong> Queue image or video processing jobs after upload without blocking user interactions.</li>
          <li><strong>Email and messaging:</strong> Fan out transactional communications in the background after account or billing events.</li>
          <li><strong>Audit and compliance:</strong> Persist audit events quickly and process enrichment workflows out of band.</li>
          <li><strong>Third-party integrations:</strong> Buffer calls to external APIs so transient provider issues do not break core application flows.</li>
        </ul>

        <p>
          The main benefits of this model are:
        </p>

        <ul>
          <li><strong>Resilience:</strong> Temporary failures are retried without losing accepted work.</li>
          <li><strong>Elastic scaling:</strong> Lambda scales consumers based on queue depth and throughput needs.</li>
          <li><strong>Smoother user experience:</strong> APIs can acknowledge requests quickly instead of waiting on slow background tasks.</li>
          <li><strong>Fault isolation:</strong> A failing downstream dependency does not immediately take out the whole request path.</li>
          <li><strong>Operational control:</strong> DLQs and queue metrics provide clear recovery and monitoring touchpoints.</li>
        </ul>

        <Divider className="divider" />

        <h2>Provisioning with AWS CDK (TypeScript)</h2>

        <p>
          The stack below creates a main queue, a DLQ, and a Lambda consumer wired via SQS event source mapping.
        </p>

        <pre>
          <code>
            {`import * as cdk from "aws-cdk-lib";
import { Construct } from "constructs";
import * as sqs from "aws-cdk-lib/aws-sqs";
import * as lambda from "aws-cdk-lib/aws-lambda";
import * as eventSources from "aws-cdk-lib/aws-lambda-event-sources";
import * as iam from "aws-cdk-lib/aws-iam";

export class OrdersProcessingStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props?: cdk.StackProps) {
    super(scope, id, props);

    const ordersDlq = new sqs.Queue(this, "OrdersDlq", {
      queueName: "orders-dlq",
      retentionPeriod: cdk.Duration.days(14)
    });

    const ordersQueue = new sqs.Queue(this, "OrdersQueue", {
      queueName: "orders-queue",
      visibilityTimeout: cdk.Duration.seconds(90),
      deadLetterQueue: {
        queue: ordersDlq,
        maxReceiveCount: 5
      }
    });

    const ordersConsumer = new lambda.Function(this, "OrdersConsumer", {
      runtime: lambda.Runtime.NODEJS_20_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset("lambda/orders-consumer"),
      timeout: cdk.Duration.seconds(30),
      memorySize: 256,
      environment: {
        ORDERS_QUEUE_URL: ordersQueue.queueUrl
      }
    });

    ordersConsumer.addEventSource(
      new eventSources.SqsEventSource(ordersQueue, {
        batchSize: 10,
        maxBatchingWindow: cdk.Duration.seconds(5),
        reportBatchItemFailures: true
      })
    );

    ordersQueue.grantConsumeMessages(ordersConsumer);

    ordersConsumer.addToRolePolicy(
      new iam.PolicyStatement({
        actions: ["cloudwatch:PutMetricData"],
        resources: ["*"]
      })
    );

    new cdk.CfnOutput(this, "OrdersQueueUrl", {
      value: ordersQueue.queueUrl
    });
  }
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Lambda consumer example (Node.js)</h2>

        <p>
          This consumer processes each record and uses partial batch failure response so only failed messages are retried.
        </p>

        <pre>
          <code>
            {`export const handler = async (event) => {
  const batchItemFailures = [];

  for (const record of event.Records) {
    try {
      const payload = JSON.parse(record.body);
      await processOrder(payload);
    } catch (error) {
      console.error("Failed processing message", {
        messageId: record.messageId,
        error: error.message
      });

      batchItemFailures.push({
        itemIdentifier: record.messageId
      });
    }
  }

  return { batchItemFailures };
};

async function processOrder(order) {
  if (!order.orderId || !order.customerId) {
    throw new Error("Invalid order payload");
  }

  console.log("Processing order", {
    orderId: order.orderId,
    customerId: order.customerId,
    total: order.total
  });
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Sending messages to SQS (producer snippet)</h2>

        <p>
          Your API or service can enqueue work using the AWS SDK v3:
        </p>

        <pre>
          <code>
            {`import { SQSClient, SendMessageCommand } from "@aws-sdk/client-sqs";

const sqs = new SQSClient({ region: process.env.AWS_REGION });

export async function enqueueOrder(order) {
  const command = new SendMessageCommand({
    QueueUrl: process.env.ORDERS_QUEUE_URL,
    MessageBody: JSON.stringify(order),
    MessageAttributes: {
      eventType: {
        DataType: "String",
        StringValue: "OrderCreated"
      }
    }
  });

  await sqs.send(command);
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Operational tuning tips</h2>

        <ul>
          <li>Set SQS visibility timeout to at least 6x the Lambda timeout to reduce premature retries.</li>
          <li>Use DLQs from day one and set alarms on DLQ message count.</li>
          <li>Use partial batch response (<code>reportBatchItemFailures</code>) to avoid replaying successful records.</li>
          <li>Add idempotency keys for writes to external systems.</li>
          <li>Track queue depth and age of oldest message to detect backlog early.</li>
        </ul>

        <Divider className="divider" />

        <h2>Quick test flow</h2>

        <p>
          After deploying your stack, send a test message and watch your Lambda logs:
        </p>

        <pre>
          <code>
            {"aws sqs send-message \
  --queue-url \"$ORDERS_QUEUE_URL\" \
  --message-body '{\"orderId\":\"o-123\",\"customerId\":\"c-001\",\"total\":42.50}'"}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Common gotchas</h2>

        <ul>
          <li><strong>Visibility timeout is too short:</strong> Messages can reappear while a Lambda invocation is still running, causing duplicate processing.</li>
          <li><strong>No idempotency strategy:</strong> SQS and Lambda are at-least-once delivery, so duplicate messages are expected and must be handled safely.</li>
          <li><strong>Batch failure handling is not enabled:</strong> Without partial batch responses, one bad record can cause successful records to be retried repeatedly.</li>
          <li><strong>Missing DLQ alarms:</strong> Teams often configure a DLQ but forget to alert on message buildup, delaying incident response.</li>
          <li><strong>Overly large payloads:</strong> SQS has a 256 KB message size limit, so large objects should go to S3 with a reference in the queue message.</li>
          <li><strong>Unbounded downstream concurrency:</strong> Scaling consumers without limits can overwhelm databases or external APIs, so reserved concurrency and backpressure should be considered.</li>
        </ul>

        <Divider className="divider" />

        <h2>Final thoughts</h2>

        <p>
          Lambda plus SQS is a simple and scalable pattern for asynchronous workflows in serverless systems. With the right timeout settings, DLQ strategy, and observability, it handles bursty traffic and failures gracefully.
        </p>

        <p>
          If you are building event-driven services on AWS, this is one of the best patterns to standardize across teams.
        </p>
      </div>
    );
  }

  return <Article
    article={LambdaSqsEventProcessingDetails}
    content={ArticleContent}
  />;
}

export default LambdaSqsEventProcessing;
